import * as React from "react";
import {
    Form, Link,
    Params, redirect,
    useActionData,
    useLoaderData, useNavigate,
} from "react-router-dom";
import {
    Breadcrumb,
    BreadcrumbDivider,
    BreadcrumbSection,
    Button,
    Grid,
    GridColumn, Header,
    Message,
} from "semantic-ui-react";
import "./project-edit.css";
import ConstructionPermitEdit from "./construction-permit-edit";
import { Constants } from "../../../globals/constants";
import LandUsePlanChangeEdit from "./land-use-plan-change-edit";
import { RootStore } from "../../../stores/root-store";
import LandfillSiteEdit from "./landfill-site-edit";
import DezoningEdit from "./dezoning-edit";
import SoilImprovementEdit from "./soil-improvement-edit";
import { KeyValueStore } from "../../../stores/key-value/key-value-store";
import { AuthenticationStore } from "../../../stores/authentication/authentication-store";
import { Contact } from "../../../stores/contact/contact-store";
import { Project } from "../../../stores/project/project";
import {
    CreationProjectDto,
    ProjectDtoFactory,
    ProjectDtoValidator,
    UsageProjectDto
} from "../../../stores/project/project-dto";
import {
    ConstructionPermitProject,
    LandfillSiteProject,
    LandUsePlanChangeProject
} from "../../../stores/project/project";
import { DezoningProject, SoilImprovementProject } from "../../../stores/project/project";
import { Municipality } from "../../../stores/municipality/municipality-store";
import { updateCreationStatus } from "../../../stores/area/creation-area-update";

type LoadResult = {
    project: Project;
    municipalities: Municipality[];
    contacts: Contact[];
    keyValueStore: KeyValueStore;
    authenticationStore: AuthenticationStore
};

export async function loader(
    params: Params,
    rootStore: RootStore
) {
    if (params.projectCategory !== undefined && params.projectId !== undefined) {
        const projectId = parseInt(params.projectId) || 0;
        const projectCategory = params.projectCategory;

        try {
            let project: Project;
            const rootStoreSession = rootStore.newSession();

            if (projectCategory === Constants.projectCategoryCreation) {
                project = await rootStoreSession.projectStore.loadCreationProject(projectId);
            } else {
                project = await rootStoreSession.projectStore.loadUsageProject(projectId);
                await rootStoreSession.compensationStore.loadWithOptions({ usageProjectId: project.id });

                for (const c of project.compensations) {
                    await rootStoreSession.projectStore.loadCreationProject(c.creationProjectId);
                }
            }

            const contacts = await rootStoreSession.contactStore.loadAll();
            const municipalities = await rootStoreSession.municipalityStore.loadAll();
            const keyValueStore = rootStore.keyValueStore;
            const authenticationStore = rootStore.authenticationStore;
            return { project, municipalities, contacts, keyValueStore, authenticationStore };
        } catch (e) {
            throw new Response("", {
                status: e.statusCode,
                statusText: e.message,
            });
        }
    }

    throw new Response("", {
        status: 404,
        statusText: "Projekt wurde nicht gefunden",
    });
}

export async function action(
    request: Request,
    rootStore: RootStore
) {
    const formData = await request.formData();
    const rootStoreSession = rootStore.session;
    const projectDto = ProjectDtoFactory.makeProjectDto(formData, rootStoreSession.municipalityStore, rootStore.keyValueStore);
    const valid = ProjectDtoValidator.checkRequiredFields(projectDto);

    if (valid) {
        try {
            const rootStoreSession = rootStore.session;

            if (projectDto.category === Constants.projectCategoryCreation) {
                const oldProjectStatus = rootStoreSession.projectStore.creationProjects.find(p => p.id == projectDto.id)?.projectDto.status;
                const project = await rootStoreSession.projectStore.updateCreationProject(projectDto as CreationProjectDto);
                const creationAreas = await rootStoreSession.areaStore.loadCreationAreas({ projectId: project.id });

                if (projectDto.status !== oldProjectStatus) {
                    await updateCreationStatus(creationAreas, rootStoreSession);
                }

                return redirect(`/projekte/${project.categoryKey}/${project.id}`);
            } else {
                const oldProjectStatus = rootStoreSession.projectStore.usageProjects.find(p => p.id == projectDto.id)?.projectDto.status;
                const project = await rootStoreSession.projectStore.updateUsageProject(projectDto as UsageProjectDto);

                if (projectDto.type === Constants.projectTypeLandfillSite) {
                    formData.set("projectType", Constants.projectTypeRecultivation);
                    const recultivationProject = (project as LandfillSiteProject).recultivationProject;
                    formData.set("id", recultivationProject.id.toString());
                    const creationProjectDto = ProjectDtoFactory.makeProjectDto(formData, rootStoreSession.municipalityStore, rootStore.keyValueStore);
                    await rootStoreSession.projectStore.updateCreationProject(creationProjectDto as CreationProjectDto);
                    const creationAreas = await rootStoreSession.areaStore.loadCreationAreas({ projectId: recultivationProject.id });

                    if (projectDto.status !== oldProjectStatus) {
                        await updateCreationStatus(creationAreas, rootStoreSession);
                    }
                }

                return redirect(`/projekte/${project.categoryKey}/${project.id}`);
            }
        } catch (e) {
            switch (e.statusCode) {
                case 400:
                    return "Es sind nicht alle erforderlichen Felder ausgefüllt.";
                default:
                    return "Es ist ein unerwarteter Fehler aufgetreten!";
            }
        }
    }

    return "Es sind nicht alle erforderlichen Felder ausgefüllt.";
}

export default function ProjectEdit(): JSX.Element {
    const {
        project,
        municipalities,
        contacts,
        keyValueStore,
        authenticationStore
    } = useLoaderData() as LoadResult
    const errorMsg = useActionData();
    const navigate = useNavigate();

    return (
        <div className="projectEdit">
            <div>
                <Breadcrumb size="big">
                    <BreadcrumbSection><Link to="/projekte">Projekte</Link></BreadcrumbSection>
                    <BreadcrumbDivider/>
                    <BreadcrumbSection>{project.fullType}</BreadcrumbSection>
                    <BreadcrumbDivider/>
                    <BreadcrumbSection>ID {project.id}</BreadcrumbSection>
                </Breadcrumb>
                <Header>{project.name}</Header>
            </div>
            {errorMsg !== undefined && (
                <Message error={true} compact={true}>
                    {errorMsg}
                </Message>
            )}
            <Form
                method="post"
                className="projectEditForm"
                autoComplete="off">
                {project.typeKey === Constants.projectTypeConstructionPermit &&
                    <ConstructionPermitEdit project={project as ConstructionPermitProject}
                                            municipalities={municipalities}
                                            keyValueStore={keyValueStore}/>}
                {project.typeKey === Constants.projectTypeLandUsePlanChange &&
                    <LandUsePlanChangeEdit project={project as LandUsePlanChangeProject}
                                           municipalities={municipalities}
                                           keyValueStore={keyValueStore}/>}
                {project.typeKey === Constants.projectTypeLandfillSite &&
                    <LandfillSiteEdit project={project as LandfillSiteProject}
                                      municipalities={municipalities}
                                      contacts={contacts}
                                      keyValueStore={keyValueStore}/>}
                {project.typeKey === Constants.projectTypeSoilImprovement &&
                    <SoilImprovementEdit project={project as SoilImprovementProject}
                                         municipalities={municipalities}
                                         contacts={contacts}
                                         keyValueStore={keyValueStore}/>}
                {project.typeKey === Constants.projectTypeDezoning &&
                    <DezoningEdit project={project as DezoningProject}
                                  municipalities={municipalities}
                                  contacts={contacts}
                                  keyValueStore={keyValueStore}/>}

                <input type="hidden" name="id" value={project.id}/>
                <input type="hidden" name="ownerId" value={authenticationStore.user?.id}/>
                <input type="hidden" name="projectType" value={project.typeKey}/>
                <Grid columns={2} relaxed={true}>
                    <GridColumn>
                        <Button
                            type="button"
                            onClick={() => {
                                navigate(-1);
                            }}
                        >
                            Abbrechen
                        </Button>
                        <Button type="submit">Speichern</Button>
                    </GridColumn>
                    <GridColumn>
                    </GridColumn>
                </Grid>
            </Form>
        </div>
    );
}
